/**
 * The event form's one validator (plan-detection handoff §0: the wizard and the from-chat review
 * share it). Pure: takes the wizard's state, returns `{ field: message }` — empty when valid.
 * `EventCreatePage` calls it from validateForm (then sets the errors), from the per-step gates,
 * and the review sheet's footer counts its keys for `FILL IN n REQUIRED`.
 */

/** A ticket link: a full URL, or at least `host.tld/...` (the wizard's rule since external ticketing shipped). */
export const TICKET_URL_RE = /^https?:\/\/.+/i;
export const BARE_HOST_RE = /^[^\s.]+\.[^\s]+/;
export const isTicketUrl = (url) => TICKET_URL_RE.test(url) || BARE_HOST_RE.test(url);

/** The field order the submit summary reads in. */
export const FIELD_ORDER = ['title', 'dateTime', 'endDateTime', 'categories', 'location', 'description', 'externalTicketUrl', 'ticketPrice', 'fundingGoal', 'numberOfGuests', 'rsvpGoal', 'fundingDeadline'];

export const FIELD_LABELS = {
  title: 'Event name',
  dateTime: 'Date & time',
  endDateTime: 'End time',
  categories: 'Categories',
  location: 'Location',
  description: 'Description',
  externalTicketUrl: 'Ticketing link',
  ticketPrice: 'Ticket price',
  fundingGoal: 'Funding goal',
  numberOfGuests: 'Number of guests',
  rsvpGoal: 'RSVP goal',
  fundingDeadline: 'Funding deadline',
};

/** Which wizard step owns each field (the step the submit jumps to on an error). */
export const FIELD_STEP = {
  title: 1, dateTime: 1, endDateTime: 1, categories: 1,
  location: 2, description: 2,
  externalTicketUrl: 3, ticketPrice: 3, fundingGoal: 3, numberOfGuests: 3, rsvpGoal: 3, fundingDeadline: 3,
};

/**
 * The Simple view's two steps (handoff "Event creator — Simple / Advanced"): 1 THE EVENT,
 * 2 TICKETS. A field missing from this map is one Simple never shows — an error on it sends
 * the host to Advanced at `FIELD_STEP[field]`.
 */
export const SIMPLE_FIELD_STEP = { title: 1, dateTime: 1, endDateTime: 1, categories: 1, location: 1, description: 1, ticketPrice: 2 };

/** THE BASICS: name, when, categories. */
export function basicsErrors(formData, now = new Date()) {
  const e = {};
  if (!(formData.title || '').trim()) e.title = 'Event name is required';
  if (!formData.dateTime) e.dateTime = 'Date and time are required';
  else if (new Date(formData.dateTime) <= now) e.dateTime = 'Event date must be in the future';
  // End time is optional, but if set it must be after the start.
  if (formData.endDateTime && formData.dateTime && new Date(formData.endDateTime) <= new Date(formData.dateTime)) e.endDateTime = 'End time must be after the start time';
  if (!formData.categories || formData.categories.length === 0) e.categories = 'Pick at least one category';
  return e;
}

/** PLACE & PITCH: where and what. A location counts once it has been picked (typed text alone is not enough when `requireCoords`). */
export function placeErrors(formData, { requireCoords = false } = {}) {
  const e = {};
  if (!(formData.location || '').trim()) e.location = 'Location is required';
  else if (requireCoords && (formData.locationLat == null || formData.locationLng == null)) e.location = 'Pick a result so the event gets its coordinates';
  if (!(formData.description || '').trim()) e.description = 'Description is required';
  return e;
}

/** THE MONEY: the ticket link, the price, the goal, the deadline. */
export function moneyErrors(formData, { isFreeEvent = false, numberOfGuests = '', rsvpEligible = false } = {}, now = new Date()) {
  const e = {};
  if (formData.isExternalTicketing) {
    // External events skip all on-platform pricing — they just need a valid link out.
    const url = (formData.externalTicketUrl || '').trim();
    if (!url) e.externalTicketUrl = 'A ticketing link is required for external events';
    else if (!isTicketUrl(url)) e.externalTicketUrl = 'Enter a valid URL (e.g. https://example.com/tickets)';
  } else if (!isFreeEvent) {
    // Pay-what-you-want: the price is a floor — $0 is valid. Only fixed-price events need a positive price.
    if (formData.pricingMode === 'pay_what_you_want') {
      if (formData.ticketPrice && parseFloat(formData.ticketPrice) < 0) e.ticketPrice = 'Minimum price can’t be negative';
    } else if (!formData.ticketPrice || parseFloat(formData.ticketPrice) <= 0) {
      e.ticketPrice = 'Ticket price must be greater than 0';
    }
    // Goal + guest count only matter for Party Starter.
    if (formData.fundingType === 'party_starter') {
      if (!formData.fundingGoal || parseFloat(formData.fundingGoal) <= 0) e.fundingGoal = 'Funding goal must be greater than 0';
      if (!numberOfGuests || parseInt(numberOfGuests, 10) < 1) e.numberOfGuests = 'Number of guests must be at least 1';
    }
  } else if (formData.fundingType === 'party_starter' && rsvpEligible) {
    // Free RSVP-goal Party Starter (Phase 10): headcount goal required.
    if (!formData.rsvpGoal || parseInt(formData.rsvpGoal, 10) < 1) e.rsvpGoal = 'Set an RSVP goal of at least 1';
  }
  // Funding deadline only matters for a PAID Party Starter — Free, External and Guaranteed don't crowdfund.
  if (!isFreeEvent && !formData.isExternalTicketing && formData.fundingType === 'party_starter') {
    if (!formData.fundingDeadline) e.fundingDeadline = 'Funding deadline is required';
    else {
      const deadlineDate = new Date(formData.fundingDeadline);
      if (deadlineDate <= now) e.fundingDeadline = 'Funding deadline must be in the future';
      if (formData.dateTime && deadlineDate > new Date(formData.dateTime)) e.fundingDeadline = 'Funding deadline must be before or equal to event date';
    }
  }
  return e;
}

/** Everything at once — what PUBLISH checks. */
export function validateEventForm(formData, opts = {}, now = new Date()) {
  return {
    ...basicsErrors(formData, now),
    ...placeErrors(formData, opts),
    ...moneyErrors(formData, opts, now),
  };
}

/** The missing fields' labels, in form order — `FILL IN 2 REQUIRED · Location, Ticket price`. */
export function missingLabels(errors) {
  return FIELD_ORDER.filter((k) => errors[k]).map((k) => FIELD_LABELS[k] || k);
}
